import React from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import pb from '../../../lib/pocketbase';

const emptyFeatureForm = {
  name: '',
  sort_order: 0,
  is_active: true,
};

function AdminSitePricingComparisonPage() {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const featureDragIndexRef = React.useRef(-1);
  const planDragIndexRef = React.useRef(-1);

  const [plans, setPlans] = React.useState([]);
  const [features, setFeatures] = React.useState([]);
  const [valuesIndex, setValuesIndex] = React.useState({});

  const [dirtyCells, setDirtyCells] = React.useState({});

  const [openFeatureModal, setOpenFeatureModal] = React.useState(false);
  const [editingFeature, setEditingFeature] = React.useState(null);
  const [featureForm, setFeatureForm] = React.useState(emptyFeatureForm);

  const fetchAll = async () => {
    setError('');
    setLoading(true);
    try {
      const [plansRes, featsRes, valsRes] = await Promise.all([
        pb.collection('site_pricing_plans').getList(1, 200, { sort: 'sort_order, name' }),
        pb.collection('site_pricing_comparison_features').getList(1, 500, { sort: 'sort_order, name' }),
        pb.collection('site_pricing_comparison_values').getList(1, 5000, {
          expand: 'feature_id,plan_id',
        }),
      ]);

      const nextPlans = (plansRes?.items ?? []).filter((p) => p);
      const nextFeatures = (featsRes?.items ?? []).filter((f) => f);

      const idx = {};
      (valsRes?.items ?? []).forEach((rec) => {
        const featureId = rec?.feature_id;
        const planId = rec?.plan_id;
        if (!featureId || !planId) return;
        const key = `${featureId}:${planId}`;
        idx[key] = {
          id: rec.id,
          feature_id: featureId,
          plan_id: planId,
          value: rec?.value ?? '',
        };
      });

      setPlans(nextPlans);
      setFeatures(nextFeatures);
      setValuesIndex(idx);
      setDirtyCells({});
    } catch (e) {
      setError(e?.message || 'Erreur chargement comparatif');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchAll();
  }, []);

  const persistFeatureOrder = async (ordered) => {
    try {
      await Promise.all(
        ordered.map((rec, idx) => pb.collection('site_pricing_comparison_features').update(rec.id, { sort_order: idx + 1 }))
      );
    } catch (e) {
      alert(e?.message || 'Erreur de ré-ordre');
    }
  };

  const persistPlanOrder = async (ordered) => {
    try {
      await Promise.all(
        ordered.map((rec, idx) => pb.collection('site_pricing_plans').update(rec.id, { sort_order: idx + 1 }))
      );
    } catch (e) {
      alert(e?.message || 'Erreur de ré-ordre');
    }
  };

  const onFeatureDragStart = (e, index) => {
    featureDragIndexRef.current = index;
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  };

  const onFeatureDragStartById = (e, featureId) => {
    const idx = features.findIndex((f) => f?.id === featureId);
    onFeatureDragStart(e, idx);
  };

  const onFeatureDragOver = (e, overIndex) => {
    e.preventDefault();
    const from = featureDragIndexRef.current;
    if (from === -1 || from === overIndex) return;
    setFeatures((arr) => {
      const next = arr.slice();
      const [moved] = next.splice(from, 1);
      next.splice(overIndex, 0, moved);
      featureDragIndexRef.current = overIndex;
      return next;
    });
  };

  const onFeatureDragOverById = (e, featureId) => {
    const idx = features.findIndex((f) => f?.id === featureId);
    onFeatureDragOver(e, idx);
  };

  const onFeatureDrop = async () => {
    featureDragIndexRef.current = -1;
    const ordered = [...features];
    await persistFeatureOrder(ordered);
    await fetchAll();
  };

  const onPlanDragStart = (e, index) => {
    planDragIndexRef.current = index;
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  };

  const onPlanDragOver = (e, overIndex) => {
    e.preventDefault();
    const from = planDragIndexRef.current;
    if (from === -1 || from === overIndex) return;
    setPlans((arr) => {
      const next = arr.slice();
      const [moved] = next.splice(from, 1);
      next.splice(overIndex, 0, moved);
      planDragIndexRef.current = overIndex;
      return next;
    });
  };

  const onPlanDrop = async () => {
    planDragIndexRef.current = -1;
    const ordered = [...plans];
    await persistPlanOrder(ordered);
    await fetchAll();
  };

  const openCreateFeature = () => {
    setEditingFeature(null);
    setFeatureForm({
      ...emptyFeatureForm,
      sort_order: features.length ? Math.max(...features.map((f) => Number(f.sort_order || 0))) + 1 : 0,
    });
    setOpenFeatureModal(true);
  };

  const openEditFeature = (f) => {
    setEditingFeature(f);
    setFeatureForm({
      name: f?.name || '',
      sort_order: Number(f?.sort_order || 0),
      is_active: f?.is_active !== false,
    });
    setOpenFeatureModal(true);
  };

  const closeFeatureModal = () => {
    setOpenFeatureModal(false);
    setEditingFeature(null);
    setFeatureForm(emptyFeatureForm);
  };

  const saveFeature = async () => {
    setLoading(true);
    try {
      const payload = {
        name: featureForm.name || '',
        sort_order: Number(featureForm.sort_order || 0),
        is_active: !!featureForm.is_active,
      };
      if (editingFeature?.id) {
        await pb.collection('site_pricing_comparison_features').update(editingFeature.id, payload);
      } else {
        await pb.collection('site_pricing_comparison_features').create(payload);
      }
      await fetchAll();
      closeFeatureModal();
    } catch (e) {
      alert(e?.message || "Erreur d'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const deleteFeature = async (id) => {
    if (!window.confirm('Supprimer cette fonctionnalité ?')) return;
    setLoading(true);
    try {
      await pb.collection('site_pricing_comparison_features').delete(id);
      await fetchAll();
      closeFeatureModal();
    } catch (e) {
      alert(e?.message || 'Erreur suppression');
    } finally {
      setLoading(false);
    }
  };

  const setCellValue = (featureId, planId, val) => {
    const key = `${featureId}:${planId}`;
    setDirtyCells((d) => ({ ...d, [key]: String(val ?? '') }));
    setValuesIndex((idx) => {
      const existing = idx[key];
      return {
        ...idx,
        [key]: {
          id: existing?.id || '',
          feature_id: featureId,
          plan_id: planId,
          value: String(val ?? ''),
        },
      };
    });
  };

  const saveAllValues = async () => {
    const keys = Object.keys(dirtyCells);
    if (keys.length === 0) return;

    setLoading(true);
    try {
      for (const key of keys) {
        const [featureId, planId] = key.split(':');
        const nextVal = dirtyCells[key];
        const existing = valuesIndex[key];

        const payload = {
          feature_id: featureId,
          plan_id: planId,
          value: String(nextVal ?? ''),
        };

        if (existing?.id) {
          await pb.collection('site_pricing_comparison_values').update(existing.id, payload);
        } else {
          await pb.collection('site_pricing_comparison_values').create(payload);
        }
      }

      await fetchAll();
    } catch (e) {
      alert(e?.message || 'Erreur enregistrement valeurs');
    } finally {
      setLoading(false);
    }
  };

  const activeFeatures = features.filter((f) => f?.is_active !== false);

  return (
    <AdminLayout title="">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ marginTop: 0 }}>Tarifs · Comparatif</h1>
          <p style={{ marginTop: 6 }}>Gérez les lignes du tableau et les valeurs par plan.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
          <button className="btn" onClick={openCreateFeature} disabled={loading}>Nouvelle ligne</button>
          <button className="btn btn-solid" onClick={saveAllValues} disabled={loading || Object.keys(dirtyCells).length === 0}>
            {Object.keys(dirtyCells).length ? `Enregistrer (${Object.keys(dirtyCells).length})` : 'Enregistrer'}
          </button>
        </div>
      </div>

      <style>{`
        .btn{ cursor:pointer; border:1px solid var(--admin-border); background: transparent; color: var(--admin-text); padding: 10px 12px; border-radius: 10px; }
        .btn:hover{ background: rgba(255,255,255,0.06); }
        .admin-shell.admin-light .btn:hover{ background: rgba(2,6,23,0.06); }
        .btn-solid{ border-color: rgba(79,70,229,0.6); background: linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85)); color:#fff; }
        .btn-solid:disabled{ opacity: .6; cursor: not-allowed; }

        .comparison-wrap{ margin-top: 12px; overflow-x: auto; border: 1px solid var(--admin-border); border-radius: 12px; }
        .comparison-table{ width: 100%; border-collapse: collapse; min-width: 880px; }
        .comparison-table th, .comparison-table td{ padding: 10px 12px; border-bottom: 1px solid var(--admin-border); vertical-align: middle; }
        .comparison-table thead th{ position: sticky; top: 0; background: var(--admin-panel); z-index: 1; }
        .comparison-table tbody tr:hover td{ background: rgba(255,255,255,0.03); }
        .admin-shell.admin-light .comparison-table tbody tr:hover td{ background: rgba(2,6,23,0.03); }

        .drag-handle{ cursor: grab; user-select:none; display:inline-flex; align-items:center; gap:8px; }
        .drag-handle:active{ cursor: grabbing; }
        .plan-drag-handle{ cursor: grab; user-select:none; }

        .cell-input{ width: 100%; background: var(--admin-panel-2); border: 1px solid var(--admin-border); color: var(--admin-text); border-radius: 10px; padding: 8px 10px; outline: none; }
        .cell-input:focus{ border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }

        .feature-actions{ display:flex; gap: 8px; align-items: center; justify-content: flex-end; }
        .linkbtn{ cursor:pointer; background: transparent; border:1px solid transparent; color: var(--admin-text); padding:6px 8px; border-radius: 10px; }
        .linkbtn:hover{ background: rgba(255,255,255,0.06); border-color: var(--admin-border); }
        .admin-shell.admin-light .linkbtn:hover{ background: rgba(2,6,23,0.06); }

        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; }
        .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 12px; width: 100%; max-width: 720px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid var(--admin-border); }
        .modal-body{ padding: 16px 20px; display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .modal-body .full{ grid-column: 1 / -1; }
        .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 8px; border-radius: 6px; }
        .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .modal-actions { display:flex; gap: 12px; justify-content:flex-end; padding: 16px 20px; border-top:1px solid var(--admin-border); }
        .field{ display:flex; flex-direction:column; gap: 6px; }
        .field label{ font-size: .9rem; color: var(--admin-muted); }
        .field input[type="text"], .field input[type="number"], .field textarea, .field select{
          background: var(--admin-panel-2);
          border:1px solid var(--admin-border);
          color: var(--admin-text);
          border-radius: 10px;
          padding: 10px 10px;
          outline:none;
        }
        .field input:focus, .field textarea:focus, .field select:focus{ border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
      `}</style>

      {error && <div style={{ color: '#ef4444', marginTop: 10 }}>{error}</div>}
      {loading && <div style={{ opacity: 0.8, marginTop: 10 }}>Chargement…</div>}

      <div className="comparison-wrap">
        <table className="comparison-table">
          <thead>
            <tr>
              <th style={{ minWidth: 280 }}>Fonctionnalités</th>
              {plans.map((p, idx) => (
                <th
                  key={p.id}
                  style={{ minWidth: 180 }}
                  draggable
                  onDragStart={(e) => onPlanDragStart(e, idx)}
                  onDragOver={(e) => onPlanDragOver(e, idx)}
                  onDrop={(e) => {
                    e.preventDefault();
                    onPlanDrop();
                  }}
                  title="Glissez pour réordonner les plans"
                >
                  <div className="plan-drag-handle">{p.name || '—'}</div>
                </th>
              ))}
              <th style={{ width: 140, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {activeFeatures.map((f) => (
              <tr
                key={f.id}
                draggable
                onDragStart={(e) => onFeatureDragStartById(e, f.id)}
                onDragOver={(e) => onFeatureDragOverById(e, f.id)}
                onDrop={(e) => {
                  e.preventDefault();
                  onFeatureDrop();
                }}
                title="Glissez pour réordonner"
              >
                <td>
                  <div className="drag-handle" aria-label="Réordonner">
                    <span style={{ opacity: 0.75 }}>⠿</span>
                    <span style={{ fontWeight: 700 }}>{f.name || '—'}</span>
                  </div>
                  <div style={{ opacity: 0.75, fontSize: '.85rem' }}>Ordre: {Number(f.sort_order || 0)}</div>
                </td>
                {plans.map((p) => {
                  const key = `${f.id}:${p.id}`;
                  const cell = valuesIndex[key];
                  return (
                    <td key={key}>
                      <input
                        className="cell-input"
                        value={cell?.value ?? ''}
                        onChange={(e) => setCellValue(f.id, p.id, e.target.value)}
                        placeholder="ex: Oui, Non, ✓, 15 pages…"
                      />
                    </td>
                  );
                })}
                <td>
                  <div className="feature-actions">
                    <button className="linkbtn" onClick={() => openEditFeature(f)} title="Modifier">✏️</button>
                  </div>
                </td>
              </tr>
            ))}

            {activeFeatures.length === 0 && (
              <tr>
                <td colSpan={plans.length + 2} style={{ padding: 18, textAlign: 'center', opacity: 0.8 }}>
                  Aucune ligne active. Ajoute une fonctionnalité avec “Nouvelle ligne”.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {openFeatureModal && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target?.classList?.contains('modal-overlay')) closeFeatureModal();
          }}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingFeature?.id ? 'Modifier la ligne' : 'Nouvelle ligne'}</h3>
              <button className="close-btn" onClick={closeFeatureModal} aria-label="Fermer">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="field full">
                <label>Nom</label>
                <input type="text" value={featureForm.name} onChange={(e) => setFeatureForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="field">
                <label>Ordre (sort_order)</label>
                <input type="number" value={featureForm.sort_order} onChange={(e) => setFeatureForm((f) => ({ ...f, sort_order: e.target.value }))} />
              </div>
              <div className="field">
                <label>Actif</label>
                <input type="checkbox" checked={featureForm.is_active} onChange={(e) => setFeatureForm((f) => ({ ...f, is_active: e.target.checked }))} />
              </div>
            </div>
            <div className="modal-actions">
              {editingFeature?.id && (
                <button className="btn" onClick={() => deleteFeature(editingFeature.id)} disabled={loading}>Supprimer</button>
              )}
              <button className="btn" onClick={closeFeatureModal} disabled={loading}>Annuler</button>
              <button className="btn btn-solid" onClick={saveFeature} disabled={loading}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminSitePricingComparisonPage;
